// Ventas — lo que la organización cobró con Stripe (TKT-00223).
// Fuente única: GET /api/v1/admin/invoicing/ventas (org del JWT, solo admins).
// Regla: esta pantalla NO suma dinero; cobrado, ticket promedio, series y
// conteos llegan calculados del servidor (por moneda).

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import {
  AlertTriangle, ArrowLeft, BadgeCheck, CircleDollarSign, Clock, Download, ExternalLink, FileWarning,
  Receipt, RefreshCw, ShoppingBag, Undo2,
} from 'lucide-react';

import { Button, Card, EmptyState, ErrorNote, Loading, PageHeader } from '@/modules/orion-shell/ui';
import { ChartBox, dinero, hoyCivil, Indicador, num, sumarDias } from '@/modules/marketing-os/shared';

import { descargarCsv, fetchVentas, type Estado, type Origen, type Venta, type VentasResp } from './api';

type Periodo = 'mes' | '30' | '90' | 'anio';
const PERIODOS: { key: Periodo; label: string }[] = [
  { key: 'mes', label: 'Este mes' },
  { key: '30', label: '30 días' },
  { key: '90', label: '90 días' },
  { key: 'anio', label: 'Este año' },
];

function rango(p: Periodo): [string, string] {
  const hoy = hoyCivil();
  if (p === 'mes') return [`${hoy.slice(0, 7)}-01`, hoy];
  if (p === 'anio') return [`${hoy.slice(0, 4)}-01-01`, hoy];
  return [sumarDias(hoy, -(Number(p) - 1)), hoy];
}

const ESTADOS: { key: Estado | ''; label: string }[] = [
  { key: '', label: 'Todas' },
  { key: 'pagado', label: 'Pagadas' },
  { key: 'pendiente', label: 'Pendientes' },
  { key: 'reembolsado', label: 'Reembolsadas' },
];

const ORIGENES: { key: Origen | ''; label: string }[] = [
  { key: '', label: 'Todas las fuentes' },
  { key: 'evento', label: 'Eventos y cursos' },
  { key: 'reserva', label: 'Reservas de viaje' },
  { key: 'expo', label: 'Expositores' },
];

const CHIP_ESTADO: Record<Estado, string> = {
  pagado: 'bg-primary/10 text-primary',
  pendiente: 'border border-border bg-muted text-foreground',
  reembolsado: 'bg-muted text-muted-foreground',
};

function fechaHora(iso: string | null, zona: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-MX', {
    timeZone: zona, day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function fechaCorta(iso: string | null, zona: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-MX', { timeZone: zona, day: 'numeric', month: 'short' });
}

function etiquetaPeriodo(p: string, porMes: boolean): string {
  const d = new Date(porMes ? `${p}-15T12:00:00Z` : `${p}T12:00:00Z`);
  return porMes
    ? d.toLocaleDateString('es-MX', { month: 'short', year: '2-digit', timeZone: 'UTC' })
    : d.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

function Chip({ className, children }: { className: string; children: React.ReactNode }) {
  return (
    <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${className}`}>
      {children}
    </span>
  );
}

function FacturaChip({ v }: { v: Venta }) {
  if (v.factura_pendiente) {
    return (
      <Chip className="bg-destructive/10 text-destructive">
        <FileWarning size={12} aria-hidden /> {v.factura.estado_label}
      </Chip>
    );
  }
  if (v.factura.estado === 'timbrada') {
    return (
      <Chip className="bg-primary/10 text-primary">
        <BadgeCheck size={12} aria-hidden /> Facturada
      </Chip>
    );
  }
  return null;
}

function Segmentos<T extends string>({
  opciones, valor, onChange, etiqueta,
}: { opciones: { key: T; label: string }[]; valor: T; onChange: (v: T) => void; etiqueta: string }) {
  return (
    <div className="flex max-w-full flex-wrap gap-0.5 rounded-xl border border-border bg-card p-0.5" role="group" aria-label={etiqueta}>
      {opciones.map((o) => (
        <button
          key={o.key || 'todos'}
          type="button"
          onClick={() => onChange(o.key)}
          aria-pressed={valor === o.key}
          className={`rounded-[10px] px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
            valor === o.key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Dato({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 break-words text-sm text-foreground">{children}</dd>
    </div>
  );
}

function DetalleVenta({ v, zona, onBack }: { v: Venta; zona: string; onBack: () => void }) {
  return (
    <Card
      title={v.concepto}
      action={
        <Button variant="ghost" onClick={onBack} className="lg:hidden" aria-label="Regresar a la lista">
          <ArrowLeft size={16} aria-hidden /> Regresar
        </Button>
      }
    >
      <div className="space-y-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <span className="text-[28px] font-semibold leading-none tracking-tight tabular-nums text-foreground">
            {dinero(v.amount_cents, v.currency)}
          </span>
          <div className="flex flex-wrap gap-1.5">
            <Chip className={CHIP_ESTADO[v.estado]}>{v.estado_label}</Chip>
            {v.stripe.modo === 'test' && <Chip className="border border-border bg-muted text-muted-foreground">Modo prueba</Chip>}
          </div>
        </div>

        {v.factura_pendiente && (
          <div className="flex gap-2 rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive" role="note">
            <FileWarning size={18} className="mt-0.5 shrink-0" aria-hidden />
            <div>
              <p className="font-semibold">Factura pendiente: el cliente pidió factura y no se ha timbrado.</p>
              {v.factura.motivo && <p className="mt-0.5 text-xs leading-relaxed">{v.factura.motivo}</p>}
            </div>
          </div>
        )}

        <dl className="grid gap-4 sm:grid-cols-2">
          <Dato label="Detalle">{v.detalle || '—'}</Dato>
          <Dato label="Fuente">{v.origen_label}</Dato>
          <Dato label="Comprador">{v.comprador.nombre || '—'}</Dato>
          <Dato label="Correo">{v.comprador.correo || '—'}</Dato>
          <Dato label="Empresa">{v.comprador.empresa || '—'}</Dato>
          <Dato label="Método de pago">{v.metodo_label}</Dato>
          <Dato label="Pagado">{fechaHora(v.pagado_en, zona)}</Dato>
          <Dato label="Creado">{fechaHora(v.creado_en, zona)}</Dato>
          {v.monto_lista_cents !== v.amount_cents && (
            <Dato label="Precio de lista">{dinero(v.monto_lista_cents, v.currency)}</Dato>
          )}
          {v.codigo_descuento && <Dato label="Código de descuento">{v.codigo_descuento}</Dato>}
        </dl>

        <div className="space-y-2 rounded-xl border border-border p-3">
          <div className="flex items-center gap-2">
            <Receipt size={16} className="text-muted-foreground" aria-hidden />
            <span className="text-sm font-semibold text-foreground">Factura (CFDI)</span>
            <span className="ml-auto"><FacturaChip v={v} /></span>
          </div>
          <dl className="grid gap-3 sm:grid-cols-2">
            <Dato label="Estado">{v.factura.estado_label}</Dato>
            <Dato label="Datos fiscales">{v.factura.datos_fiscales ? 'Capturados' : 'No capturados'}</Dato>
            {v.factura.rfc && <Dato label="RFC">{v.factura.rfc}</Dato>}
            {v.factura.razon_social && <Dato label="Razón social">{v.factura.razon_social}</Dato>}
            {v.factura.cfdi_uuid && <Dato label="Folio fiscal">{v.factura.cfdi_uuid}</Dato>}
          </dl>
          {!v.factura_pendiente && v.factura.motivo && <p className="text-xs text-muted-foreground">{v.factura.motivo}</p>}
        </div>

        <div className="space-y-2">
          <p className="break-all text-xs text-muted-foreground">
            Referencia de Stripe: {v.stripe.payment_intent || v.stripe.session_id || 'sin referencia'}
          </p>
          {v.stripe.url ? (
            <a
              href={v.stripe.url}
              target="_blank"
              rel="noopener noreferrer"
              className="orion-btn-brand inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold"
            >
              <ExternalLink size={14} aria-hidden /> Ver pago en Stripe
            </a>
          ) : (
            <p className="text-xs text-muted-foreground">Este cobro aún no tiene un pago en Stripe que abrir.</p>
          )}
        </div>
      </div>
    </Card>
  );
}

export function VentasView() {
  const [periodo, setPeriodo] = useState<Periodo>('mes');
  const [estado, setEstado] = useState<Estado | ''>('');
  const [origen, setOrigen] = useState<Origen | ''>('');
  const [data, setData] = useState<VentasResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sel, setSel] = useState<string | null>(null);
  const [csvMsg, setCsvMsg] = useState<string | null>(null);
  const [bajando, setBajando] = useState(false);

  const filtros = useMemo(() => {
    const [desde, hasta] = rango(periodo);
    return { desde, hasta, estado, origen };
  }, [periodo, estado, origen]);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetchVentas(filtros));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron cargar las ventas.');
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  const bajarCsv = async () => {
    setBajando(true);
    setCsvMsg(null);
    try {
      await descargarCsv(filtros);
    } catch (e) {
      setCsvMsg(e instanceof Error ? e.message : 'No se pudo descargar el CSV.');
    } finally {
      setBajando(false);
    }
  };

  const zona = data?.periodo.zona || 'America/Mexico_City';
  const principal = data?.totales.por_moneda[0];
  const otras = data?.totales.por_moneda.slice(1) ?? [];
  const seleccion = data?.ventas.find((v) => v.id === sel) ?? null;
  const porMes = data?.totales.agrupado_por === 'mes';
  const hayCobros = principal?.por_periodo.some((p) => p.cobrado_cents > 0) ?? false;

  return (
    <div className="mx-auto w-full max-w-6xl p-4 sm:p-6">
      <PageHeader
        title="Ventas"
        subtitle="Lo que tu organización cobró con Stripe: quién pagó, cuánto, cómo y si ya tiene factura."
        actions={
          <>
            <Segmentos etiqueta="Periodo" opciones={PERIODOS} valor={periodo} onChange={setPeriodo} />
            <Button onClick={() => void bajarCsv()} disabled={bajando || !data?.total_ventas}>
              <Download size={16} aria-hidden /> CSV
            </Button>
            <Button variant="ghost" onClick={() => void cargar()} aria-label="Actualizar">
              <RefreshCw size={16} aria-hidden className={loading ? 'animate-spin motion-reduce:animate-none' : ''} />
            </Button>
          </>
        }
      />

      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
        <Segmentos etiqueta="Estado de la venta" opciones={ESTADOS} valor={estado} onChange={(v) => { setEstado(v); setSel(null); }} />
        <label className="sr-only" htmlFor="ventas-origen">Fuente</label>
        <select
          id="ventas-origen"
          value={origen}
          onChange={(e) => { setOrigen(e.target.value as Origen | ''); setSel(null); }}
          className="h-10 rounded-xl border border-border bg-card px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:ml-auto"
        >
          {ORIGENES.map((o) => <option key={o.key || 'todas'} value={o.key}>{o.label}</option>)}
        </select>
      </div>

      {csvMsg && <div className="mb-3"><ErrorNote>{csvMsg}</ErrorNote></div>}

      {loading && !data ? (
        <Loading label="Cargando ventas…" />
      ) : error ? (
        <div className="space-y-3 py-4">
          <ErrorNote>{error}</ErrorNote>
          <Button onClick={() => void cargar()}><RefreshCw size={14} aria-hidden /> Reintentar</Button>
        </div>
      ) : data ? (
        <div className="space-y-4">
          {data.totales.facturas_pendientes > 0 && (
            <div className="flex gap-3 rounded-[20px] border border-destructive/30 bg-destructive/10 p-4 text-destructive" role="alert">
              <FileWarning size={20} className="mt-0.5 shrink-0" aria-hidden />
              <div className="min-w-0 text-sm">
                <p className="font-semibold">
                  {data.totales.facturas_pendientes === 1
                    ? '1 venta pagada tiene la factura pendiente'
                    : `${num(data.totales.facturas_pendientes)} ventas pagadas tienen la factura pendiente`}
                </p>
                <p className="mt-0.5 text-xs leading-relaxed">
                  {!data.emisor_fiscal.configurado
                    ? 'Tu organización todavía no tiene emisor fiscal (CFDI) configurado, así que las facturas que pidieron tus clientes NO se timbraron. Configura el emisor o factúralas por fuera.'
                    : !data.emisor_fiscal.listo
                      ? 'El emisor fiscal está pendiente de CSD: las facturas solicitadas no se han timbrado.'
                      : 'Revisa cada venta marcada para ver por qué no se timbró.'}
                </p>
              </div>
            </div>
          )}

          {data.totales.modo_prueba.ventas > 0 && (
            <p className="flex items-center gap-2 rounded-xl border border-border bg-muted px-3 py-2 text-xs text-muted-foreground">
              <AlertTriangle size={14} aria-hidden />
              {num(data.totales.modo_prueba.ventas)} cobro(s) de una cuenta de Stripe en modo prueba no cuentan en los totales.
            </p>
          )}

          {principal ? (
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Indicador icon={<CircleDollarSign size={18} />} label="Cobrado" value={dinero(principal.cobrado_cents, principal.currency)}
                sub={principal.currency} tone="violet" />
              <Indicador icon={<ShoppingBag size={18} />} label="Ventas pagadas" value={num(principal.ventas_pagadas)}
                sub={principal.lugares_pagados ? `${num(principal.lugares_pagados)} lugares` : undefined} tone="indigo" />
              <Indicador icon={<Receipt size={18} />} label="Ticket promedio"
                value={dinero(principal.ticket_promedio_cents, principal.currency)} tone="blue" />
              <Indicador icon={<Clock size={18} />} label="Por cobrar" value={dinero(principal.pendiente_cents, principal.currency)}
                sub={
                  <span className="inline-flex items-center gap-1">
                    {num(principal.ventas_pendientes)} pendiente(s)
                    {principal.ventas_reembolsadas > 0 && (
                      <> · <Undo2 size={12} aria-hidden /> {dinero(principal.reembolsado_cents, principal.currency)} reembolsado</>
                    )}
                  </span>
                }
                tone="purple" />
            </div>
          ) : null}

          {otras.length > 0 && (
            <p className="text-xs text-muted-foreground">
              También cobraste en otras monedas: {otras.map((m) => `${dinero(m.cobrado_cents, m.currency)} (${num(m.ventas_pagadas)} ventas)`).join(' · ')}.
              Las monedas no se suman entre sí.
            </p>
          )}

          {principal && hayCobros && (
            <Card title={`Cobrado por ${porMes ? 'mes' : 'día'}`}>
              <ChartBox height={200}>
                {(width) => (
                  <BarChart width={width} height={200} data={principal.por_periodo} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke="hsl(var(--border))" />
                    <XAxis dataKey="periodo" tickLine={false} axisLine={false} minTickGap={16}
                      tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                      tickFormatter={(p: string) => etiquetaPeriodo(p, porMes)} />
                    <YAxis tickLine={false} axisLine={false} width={56}
                      tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
                      tickFormatter={(v: number) => dinero(v, principal.currency)} />
                    <Tooltip
                      cursor={{ fill: 'hsl(var(--muted))' }}
                      contentStyle={{ background: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: 8, color: 'hsl(var(--popover-foreground))', fontSize: 12 }}
                      labelFormatter={(p: string) => etiquetaPeriodo(p, porMes)}
                      formatter={(v: number) => [dinero(v, principal.currency), 'Cobrado']}
                    />
                    <Bar dataKey="cobrado_cents" fill="hsl(var(--primary))" fillOpacity={0.85} radius={[4, 4, 0, 0]} maxBarSize={28} isAnimationActive={false} />
                  </BarChart>
                )}
              </ChartBox>
              {principal.por_origen.length > 1 && (
                <ul className="mt-3 flex flex-wrap gap-2">
                  {principal.por_origen.map((o) => (
                    <li key={o.origen} className="rounded-full border border-border px-2.5 py-1 text-xs text-foreground">
                      {o.origen_label}: <span className="tabular-nums">{dinero(o.cobrado_cents, principal.currency)}</span> · {num(o.ventas)}
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          )}

          {data.ventas.length === 0 ? (
            <EmptyState icon={<ShoppingBag size={18} />} title="No hay ventas en este periodo">
              Prueba con un periodo más amplio o quita los filtros. Aquí aparecen los cobros de Stripe de eventos,
              reservas y expositores en cuanto se pagan.
            </EmptyState>
          ) : (
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
              <section aria-label="Lista de ventas" className={seleccion ? 'hidden lg:block' : ''}>
                <Card
                  title={`${num(data.total_ventas)} venta${data.total_ventas === 1 ? '' : 's'}`}
                  bodyClassName="px-2 sm:px-3"
                >
                  <ul className="divide-y divide-border">
                    {data.ventas.map((v) => (
                      <li key={v.id}>
                        <button
                          type="button"
                          onClick={() => setSel(v.id)}
                          aria-current={sel === v.id ? 'true' : undefined}
                          className={`flex w-full min-w-0 items-start gap-3 rounded-xl px-2 py-3 text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                            sel === v.id ? 'bg-muted' : ''
                          }`}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold text-foreground">
                              {v.counterparty_label || 'Comprador sin nombre'}
                            </p>
                            <p className="truncate text-xs text-muted-foreground">
                              {v.concepto}{v.detalle ? ` · ${v.detalle}` : ''}
                            </p>
                            <div className="mt-1.5 flex flex-wrap gap-1.5">
                              <Chip className={CHIP_ESTADO[v.estado]}>{v.estado_label}</Chip>
                              <Chip className="border border-border text-muted-foreground">{v.metodo_label}</Chip>
                              <FacturaChip v={v} />
                            </div>
                          </div>
                          <div className="shrink-0 text-right">
                            <p className="text-sm font-semibold tabular-nums text-foreground">{dinero(v.amount_cents, v.currency)}</p>
                            <p className="text-xs text-muted-foreground">{fechaCorta(v.occurred_at, zona)}</p>
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                  {data.mostradas < data.total_ventas && (
                    <p className="px-2 pt-2 text-xs text-muted-foreground">
                      Mostrando las {num(data.mostradas)} más recientes de {num(data.total_ventas)}. Los totales sí incluyen todas; descarga el CSV para verlas completas.
                    </p>
                  )}
                </Card>
              </section>
              <section aria-label="Detalle de la venta" className={seleccion ? '' : 'hidden lg:block'}>
                {seleccion ? (
                  <div className="lg:sticky lg:top-4">
                    <DetalleVenta v={seleccion} zona={zona} onBack={() => setSel(null)} />
                  </div>
                ) : (
                  <EmptyState icon={<Receipt size={18} />} title="Elige una venta">
                    Verás al comprador, la factura y la liga al pago en Stripe.
                  </EmptyState>
                )}
              </section>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
